import { TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import type { AnyTRPCRouter } from '@trpc/server';

/**
 * Creates a TRPC link that reads version headers from TRPC responses.
 * This link can be shared across all client applications (admin, provider, student).
 *
 * @param onVersionDetected - Callback function called when a version header is found
 * @returns TRPC link that intercepts responses and extracts version headers
 */
export function createVersionHeaderLink<TRouter extends AnyTRPCRouter>(
  onVersionDetected?: (version: string) => void,
): TRPCLink<TRouter> {
  return () => {
    return ({ next, op }) => {
      return observable((observer) => {
        const subscription = next(op).subscribe({
          next(result) {
            // Check if this is a successful response with headers
            const response = result.context?.response;
            if (response instanceof Response) {
              const appVersion = response.headers.get('x-app-version');
              if (appVersion !== null) {
                if (onVersionDetected) {
                  onVersionDetected(appVersion);
                } else {
                  // Default behavior: log to console
                  console.info(`tRPC Response - App Version: ${appVersion}`);
                }
              }
            }

            observer.next(result);
          },
          error(err) {
            observer.error(err);
          },
          complete() {
            observer.complete();
          },
        });

        return () => subscription.unsubscribe();
      });
    };
  };
}
